import {
  formatNumber,
  CurrencyValue,
  LineChart,
  formatCurrency,
  formatDatetime,
  ASSET_SYMBOLS_PALETTE
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { TreasuryData } from './TreasuryChartComponent';
import { ChartConfig } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { ReactNode } from 'react';

export default function TreasuryChart({
  data,
  timeFilter,
  config,
  chartDaysAgo
}: {
  data: TreasuryData[];
  timeFilter: ReactNode;
  config: ChartConfig;
  chartDaysAgo: string;
}) {
  const yScaleValueFormatter = (value: string | number) =>
    `${formatNumber(Number(value))}`;

  const valueFormatter = (value: string | number) => (
    <CurrencyValue
      value={value}
      variant="dynamic"
      options={{ notation: 'standard', currency: 'USD' }}
    />
  );

  const datasets = useMemo(
    () =>
      Object.keys(
        data.reduce(
          (groupedByAsset: Record<string, boolean>, entry: TreasuryData) => {
            const { underlying_symbol, treasury } = entry;

            if (
              underlying_symbol &&
              !groupedByAsset[underlying_symbol] &&
              Number(treasury) > 0
            ) {
              groupedByAsset[underlying_symbol] = true;
            }

            return groupedByAsset;
          },
          {} as Record<string, boolean>
        )
      ).map((underlying_symbol) => {
        return {
          label: underlying_symbol,
          xField: 'date',
          yField: 'treasury',
          data: data.filter(
            (entry: TreasuryData) =>
              entry.underlying_symbol === underlying_symbol
          ),
          fill: false,
          backgroundColor: ASSET_SYMBOLS_PALETTE[underlying_symbol],
          borderColor: ASSET_SYMBOLS_PALETTE[underlying_symbol]
        };
      }),
    [data]
  );

  return (
    <LineChart
      key="treasury-income"
      config={config}
      datasets={datasets}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      timeFilter={timeFilter}
      tooltip={{
        labelFormatter: (value) => `${value}`,
        valueFormatter: (value) => `${formatCurrency(Number(value))}`,
        titleFormatter: (_value, tootlipItems) =>
          // @ts-ignore
          `${formatDatetime(tootlipItems[0].raw.x, { format: 'MMM dd, yyyy' })}`
      }}
      daysAgo={chartDaysAgo}
      showKpisAsTooltip
    />
  );
}
